import React from 'react'
import { Card, Divider, Tooltip, Popconfirm, Typography, Tag, Space } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import {
  faClock,
  faBell,
  faUsers,
  faExclamation,
  faEye,
  faUser,
  faPrint,
} from '@fortawesome/free-solid-svg-icons'
import { ActionButton } from '@/ui/ActionButton'
import styled from 'styled-components'
import { isEmpty } from 'lodash'

const { Text, Title } = Typography

const StyledCard = styled(Card)`
  border-radius: 12px;
  margin: 12px 0;
  transition: all 0.3s;
  cursor: pointer;
  &:hover {
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.1);
  }
`

interface IconWithTooltipProps {
  icon: IconProp
  text: React.ReactNode
  tooltip: React.ReactNode
}

const IconWithTooltip: React.FC<IconWithTooltipProps> = ({ icon, text, tooltip }) => (
  <Tooltip title={tooltip}>
    <Space style={{ marginRight: 16 }}>
      <FontAwesomeIcon icon={icon} style={{ color: '#555' }} />
      <Text type="secondary">{text}</Text>
    </Space>
  </Tooltip>
)

interface FollowUpCardModernProps {
  followTopic: React.ReactNode
  followMessage: React.ReactNode
  followTime: string
  entryBy: string
  followID: string | number
  followCategoryID?: any
  followStatusID?: any
  assignUserID?: any[]
  alertLocationID?: any[]
  alertDate?: any
  alertDateMessage?: string
  userSeen: Array<{ user: string }>
  handleUpdate: (payload: any) => void
  handleDelete: (followID: string | number) => void
  handleSelect: (payload: any) => void
  hideUpdate?: boolean
  hideDelete?: boolean
  hidePrint?: boolean
}

const FollowUpCardModern: React.FC<FollowUpCardModernProps> = ({
  followTopic,
  followMessage,
  followTime,
  entryBy,
  followID,
  followCategoryID,
  followStatusID,
  assignUserID,
  alertLocationID,
  alertDate,
  alertDateMessage,
  userSeen,
  handleUpdate,
  handleDelete,
  handleSelect,
  hideUpdate = false,
  hideDelete = false,
  hidePrint = true,
}) => {
  const userList = assignUserID?.map((user, index) => (
    <Tag key={index} icon={<FontAwesomeIcon icon={faUser} style={{ marginRight: 2 }} />}>
      {user}
    </Tag>
  ))
  const locationList = alertLocationID?.map((location, index) => (
    <Tag key={index} icon={<FontAwesomeIcon icon={faExclamation} style={{ marginRight: 2 }} />}>
      {location?.name}
    </Tag>
  ))
  const seenList = isEmpty(userSeen) ? 'Seen' : userSeen.map((obj) => obj.user).join(', ')

  return (
    <StyledCard
      title={
        <Title level={5} style={{ margin: 0 }}>
          {followTopic}
        </Title>
      }
      onClick={() => handleSelect({ followID })}
      extra={
        <Space>
          {!hideUpdate && (
            <ActionButton
              type='button'
              $type='edit'
              onClick={(e) => {
                e.stopPropagation()
                handleUpdate({
                  followMessage,
                  followTopic,
                  followID,
                  followCategoryID,
                  followStatusID,
                  assignUserID,
                  alertLocationID,
                  alertDate,
                })
              }}
            />
          )}
          {!hidePrint && (
            <ActionButton type='button' $type='edit' onClick={(e) => e.stopPropagation()}>
              <FontAwesomeIcon icon={faPrint} />
            </ActionButton>
          )}
          {!hideDelete && (
            <Popconfirm
              title="Are you sure you want to delete this follow up?"
              onConfirm={(event) => {
                if (event) {
                  event.stopPropagation()
                }
                handleDelete(followID)
              }}
              onCancel={(event) => {
                if (event) {
                  event.stopPropagation()
                }
              }}
              cancelButtonProps={{ style: { paddingTop: 0 } }}
            >
              <ActionButton type='button' $type='delete' onClick={(e) => e.stopPropagation()} />
            </Popconfirm>
          )}
        </Space>
      }
    >
      <Text style={{ whiteSpace: 'pre-line' }}>{followMessage}</Text>
      <Divider />
      <Space wrap size={[16, 8]}>
        <IconWithTooltip icon={faClock} text={followTime} tooltip="Create time" />
        <IconWithTooltip icon={faUser} text={entryBy} tooltip="Create user" />
        <IconWithTooltip icon={faEye} text={userSeen?.length || 0} tooltip={seenList} />
        {assignUserID?.length ? (
          <IconWithTooltip icon={faUsers} text={assignUserID.length} tooltip={userList} />
        ) : null}
        {alertLocationID?.length ? (
          <IconWithTooltip icon={faExclamation} text={alertLocationID.length} tooltip={locationList} />
        ) : null}
        {!isEmpty(alertDate) && (
          <IconWithTooltip icon={faBell} text={alertDateMessage} tooltip="Alert time" />
        )}
      </Space>
    </StyledCard>
  )
}

export default FollowUpCardModern
